import Head from "next/head";
import schemaTags from "@/data/schema-tags.json";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.dpdtech.application.mpos";

const JsonLd = ({ data }) => {
  if (!data) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

const buildSoftwareSchema = (software) => {
  if (!software) return null;

  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: software.name,
    operatingSystem: software.operatingSystem ?? "Windows, Android",
    applicationCategory: software.applicationCategory ?? "BusinessApplication",
    applicationSubCategory: software.applicationSubCategory,
    description: software.description,
    url: software.url,
    image: software.image,
    downloadUrl: software.downloadUrl ?? PLAY_STORE_URL,
    aggregateRating: software.aggregateRating ?? {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "5059",
    },
    offers: software.offers ?? {
      "@type": "Offer",
      price: software.price ?? "0",
      priceCurrency: software.priceCurrency ?? "INR",
    },
  };
};

const buildBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs?.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.item,
    })),
  };
};

const buildFaqSchema = (faqs) => {
  if (!faqs?.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.name,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.text,
      },
    })),
  };
};

/**
 * Reusable JSON-LD schema tags for page <head>.
 * Pass a `page` key from schema-tags.json, or override with software / breadcrumbs / faqs.
 */
export default function SchemaTags({ page, software, breadcrumbs, faqs }) {
  const pageSchema = page ? schemaTags[page] ?? {} : {};
  const schemas = [
    buildSoftwareSchema(software ?? pageSchema.software),
    buildBreadcrumbSchema(breadcrumbs ?? pageSchema.breadcrumbs),
    buildFaqSchema(faqs ?? pageSchema.faqs),
  ].filter(Boolean);

  if (!schemas.length) return null;

  return (
    <Head>
      {schemas.map((schema, index) => (
        <JsonLd key={schema["@type"] ?? index} data={schema} />
      ))}
    </Head>
  );
}
